import { applyCampaignMapLayouts, cacheCampaignMapLayout, type MapLayoutEdit, type UserMapDef } from '../data/map';

const API_BASE = String((import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_LEADERBOARD_API ?? '/api').replace(/\/$/, '');

/** Small best-effort client for the shared public user-map library. */
class MapService {
  private async request<T>(input: RequestInfo, init?: RequestInit): Promise<T | undefined> {
    try {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 8000);
      const response = await fetch(input, { ...init, signal: controller.signal, headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) } });
      window.clearTimeout(timeout);
      if (!response.ok) return undefined;
      return await response.json() as T;
    } catch {
      return undefined;
    }
  }

  async listPublic(): Promise<UserMapDef[]> {
    const payload = await this.request<{ maps?: UserMapDef[] }>(`${API_BASE}/maps`);
    return Array.isArray(payload?.maps) ? payload.maps : [];
  }

  async loadCampaignLayouts(): Promise<boolean> {
    const payload = await this.request<{ layouts?: Record<string, MapLayoutEdit> }>(`${API_BASE}/campaign-maps`);
    if (!payload?.layouts || typeof payload.layouts !== 'object') return false;
    applyCampaignMapLayouts(payload.layouts);
    return true;
  }

  async saveCampaignLayout(mapId: string, layout: MapLayoutEdit): Promise<boolean> {
    const payload = await this.request<{ ok?: boolean; layout?: MapLayoutEdit }>(`${API_BASE}/campaign-maps`, {
      method: 'POST', body: JSON.stringify({ mapId, layout }),
    });
    if (payload?.ok !== true) return false;
    cacheCampaignMapLayout(mapId, payload.layout ?? layout);
    return true;
  }

  async publish(map: UserMapDef): Promise<boolean> {
    const payload = await this.request<{ ok?: boolean }>(`${API_BASE}/maps`, { method: 'POST', body: JSON.stringify({ ...map, visibility: 'public' }) });
    return payload?.ok === true;
  }

  async unpublish(mapId: string): Promise<boolean> {
    const payload = await this.request<{ ok?: boolean }>(`${API_BASE}/maps?mapId=${encodeURIComponent(mapId)}`, { method: 'DELETE' });
    return payload?.ok === true;
  }

  async feedback(mapId: string): Promise<{ ratings: { sum: number; count: number }; comments: Array<{ author: string; comment: string; rating: number; timestamp: number }> }> {
    const empty = { ratings: { sum: 0, count: 0 }, comments: [] as Array<{ author: string; comment: string; rating: number; timestamp: number }> };
    const payload = await this.request<typeof empty>(`${API_BASE}/maps/${encodeURIComponent(mapId)}/feedback`);
    return payload ?? empty;
  }

  async review(mapId: string, author: string, rating: number, comment: string): Promise<boolean> {
    const payload = await this.request<{ ok?: boolean }>(`${API_BASE}/maps/${encodeURIComponent(mapId)}/feedback`, {
      method: 'POST', body: JSON.stringify({ author, rating: Math.max(1, Math.min(5, Math.round(rating))), comment }),
    });
    return payload?.ok === true;
  }
}

export const mapService = new MapService();
